import MuiDialog from '@mui/material/Dialog'
import MuiDialogTitle from '@mui/material/DialogTitle'
import MuiDialogContent from '@mui/material/DialogContent'
import MuiDialogActions from '@mui/material/DialogActions'
import MuiIconButton from '@mui/material/IconButton'
import { Close } from '@mui/icons-material'
import MuiBox from '@mui/material/Box'
import MuiDivider from '@mui/material/Divider'
import type { FormEvent, ReactElement } from 'react'
import type { Component } from '../../../../types/react'

/** ダイアログの幅サイズ */
export type DialogWidth = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

/** 共通プロパティ（両モード共通） */
interface DialogBaseProps {
  readonly open: boolean
  readonly title?: string
  readonly children: Component
  readonly actions?: Component
  readonly onClose: () => void
  readonly onSubmit?: () => void
}

/** 通常モード: width指定、fullScreenなし */
interface DialogNormalProps extends DialogBaseProps {
  readonly fullScreen?: false
  readonly width?: DialogWidth
}

/** フルスクリーンモード: fullScreen指定、widthは不要 */
interface DialogFullScreenProps extends DialogBaseProps {
  readonly fullScreen: true
  readonly width?: never
}

/** Dialogコンポーネントのプロパティ（discriminated union） */
export type DialogProps = DialogNormalProps | DialogFullScreenProps

/** fullScreenモードでない場合のデフォルト幅 */
const DEFAULT_WIDTH: DialogWidth = 'sm'

/** propsからwidth値を取得（fullScreenモードでは使用しない） */
function getWidthFromProps(dialogProps: DialogProps): DialogWidth | undefined {
  if (dialogProps.fullScreen === true) {
    return undefined
  }
  return dialogProps.width ?? DEFAULT_WIDTH
}

export default function Dialog(props: DialogProps): ReactElement {
  const { open, title, children, actions, onClose, onSubmit, fullScreen = false } = props
  const width = getWidthFromProps(props)

  // フォーム送信時にページ遷移を防ぐためpreventDefaultを呼び出す
  const handleSubmit = onSubmit
    ? (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        onSubmit()
      }
    : undefined

  // develop版と同じ構造: children + divider + actions
  const content = (
    <>
      {children}
      {actions && (
        <>
          <MuiDivider sx={{ mx: -3, my: 2 }} />
          <MuiDialogActions>{actions}</MuiDialogActions>
        </>
      )}
    </>
  )

  return (
    <MuiDialog open={open} fullWidth maxWidth={fullScreen ? false : width} fullScreen={fullScreen} onClose={onClose}>
      <MuiDialogTitle>
        <MuiBox sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <MuiBox>{title}</MuiBox>
          <MuiIconButton aria-label="閉じる" onClick={onClose}>
            <Close />
          </MuiIconButton>
        </MuiBox>
      </MuiDialogTitle>
      <MuiDialogContent dividers>
        {handleSubmit ? <form onSubmit={handleSubmit}>{content}</form> : <>{content}</>}
      </MuiDialogContent>
    </MuiDialog>
  )
}
